import {
  Lender,
  lenderSchema,
  objectIdSchema,
  paginationWithSearchSchema,
  statusCommonSchema,
} from '@hero/schema';
import { z } from 'zod';
import { API } from '..';

/**
 * Public projection of a {@link Lender} — display-safe fields only.
 *
 * Returned by `GET /lender/active` (public, JWT-authed). Excludes
 * `contacts[]`, `doc`, and anything that could leak internal state.
 * `_id` is exposed because it's the canonical identifier the carrier
 * dashboard uses when referring back to a lender (e.g. `?p=<lenderId>`
 * on the partner-view URL, and `FinancingApplication.offers[].lenderId`).
 */
export const lenderPublicSchema = lenderSchema.model.pick({
  _id: true,
  slug: true,
  name: true,
  nameAR: true,
  tagline: true,
  taglineAR: true,
  brandColor: true,
  logoIcon: true,
  website: true,
});
export type LenderPublic = z.infer<typeof lenderPublicSchema>;

/* ─────────────────────────── public ─────────────────────────── */

/**
 * `GET /lender/active` — directory of every lender whose
 * `doc.status === 'active'`, projected down to the public-safe
 * fields in {@link lenderPublicSchema}. Backs the carrier
 * dashboard's partners strip, apply-form list, and review summary.
 *
 * The `_id` is the canonical identifier — carriers refer to a
 * lender by `_id` (in `offers[].lenderId`, in partner-view URLs as
 * `?p=<lenderId>`, etc.). No `partnerId` indirection.
 */
export const lenderGetActiveAPISchema = z.object({});
export type LenderGetActiveAPI = API<
  z.infer<typeof lenderGetActiveAPISchema>,
  { lenders: LenderPublic[] }
>;

/* ─────────────────────────── admin ─────────────────────────── */

/**
 * `GET /lender/admin-all` — paginated + searchable admin list of
 * every `Lender` doc (active + blocked). Returns the full doc so
 * the admin panel can render contacts, audit metadata, etc.
 */
export const lenderGetAllAdminAPISchema = paginationWithSearchSchema.extend({
  /** Optional: filter by status. */
  status: statusCommonSchema.optional(),
});
export type LenderGetAllAdminAPI = API<
  z.infer<typeof lenderGetAllAdminAPISchema>,
  Lender[]
>;

/**
 * `GET /lender/admin-one` — single lender by id. Full doc.
 */
export const lenderGetOneAdminAPISchema = z.object({
  _id: objectIdSchema,
});
export type LenderGetOneAdminAPI = API<
  z.infer<typeof lenderGetOneAdminAPISchema>,
  Lender
>;

/**
 * `POST /lender/admin-create` — admin creates a new lender. New rows
 * default to `status: 'active'` so they're immediately visible to
 * carriers; toggle via {@link lenderSetStatusAdminAPISchema}.
 */
export const lenderCreateAdminAPISchema = lenderSchema.data;
export type LenderCreateAdminAPI = API<
  z.infer<typeof lenderCreateAdminAPISchema>,
  Lender
>;

/**
 * `PATCH /lender/admin-update` — partial update by id.
 */
export const lenderUpdateAdminAPISchema = lenderSchema.partial.extend({
  _id: objectIdSchema,
});
export type LenderUpdateAdminAPI = API<
  z.infer<typeof lenderUpdateAdminAPISchema>,
  Lender
>;

/**
 * `PATCH /lender/admin-status` — toggle `doc.status` between
 * `active` and `blocked`. Separated from the partial update so the
 * admin panel's status switch can be wired with its own loading
 * state and audit trail.
 */
export const lenderSetStatusAdminAPISchema = z.object({
  _id: objectIdSchema,
  status: z.enum(['active', 'blocked']),
});
export type LenderSetStatusAdminAPI = API<
  z.infer<typeof lenderSetStatusAdminAPISchema>,
  Lender
>;
